'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Truck, Plus, Search, Download, Star, TrendingUp, AlertTriangle, CheckCircle2, Clock, DollarSign, BarChart3, FileText, Eye, Edit, ShoppingCart, Award, ChevronRight,  } from 'lucide-react';
import { MOCK_SUPPLIERS, SUPPLIER_STATUS_STYLES, formatUGX, getPerformanceLevel, Supplier, SupplierStatus,  } from './suppliersData';
import { useRole } from '@/lib/useRole';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar,  } from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ─── Status Badge ─────────────────────────────────────────────────────────────

function SupplierStatusBadge({ status }: { status: SupplierStatus }) {
  const st = SUPPLIER_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, color, href }: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; color: string; href?: string;
}) {
  const content = (
    <div className={`bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow ${href ? 'cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={16} className="text-white" />
        </div>
        {href && <ChevronRight size={14} className="text-gray-300" />}
      </div>
      <div className="text-2xl font-black text-gray-900 mb-0.5">{value}</div>
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
  return href ? <Link href={href}>{content}</Link> : content;
}

// ─── Spend Trend Data ─────────────────────────────────────────────────────────

const SPEND_TREND = [
  { month: 'Apr', spend: 38500000 },
  { month: 'May', spend: 42000000 },
  { month: 'Jun', spend: 35800000 },
  { month: 'Jul', spend: 48200000 },
  { month: 'Aug', spend: 44500000 },
  { month: 'Sep', spend: 29500000 },
];

const SPEND_BY_CATEGORY = [
  { name: 'Feed', value: 187500000, color: '#f97316' },
  { name: 'Day-Old Chicks', value: 95000000, color: '#8b5cf6' },
  { name: 'Vaccines', value: 42000000, color: '#06b6d4' },
  { name: 'Equipment', value: 28500000, color: '#10b981' },
  { name: 'Chemicals', value: 8200000, color: '#f59e0b' },
];

const PERFORMANCE_DIST = [
  { name: 'Excellent (90+)', count: 2, color: '#10b981' },
  { name: 'Good (80-89)', count: 1, color: '#3b82f6' },
  { name: 'Satisfactory (70-79)', count: 1, color: '#f59e0b' },
  { name: 'Needs Improvement', count: 1, color: '#f97316' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SuppliersContent() {
  const { can } = useRole();
  const canCreate = can('manage_procurement') || can('manage_inventory');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    return MOCK_SUPPLIERS.filter(s => {
      const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.supplierCode.toLowerCase().includes(search.toLowerCase()) || s.categories.some(c => c.toLowerCase().includes(search.toLowerCase()));
      const matchStatus = statusFilter === 'all' || s.status === statusFilter;
      const matchCat = categoryFilter === 'all' || s.categories.includes(categoryFilter);
      return matchSearch && matchStatus && matchCat;
    });
  }, [search, statusFilter, categoryFilter]);

  const totalSpend = MOCK_SUPPLIERS.reduce((s, x) => s + x.totalSpend, 0);
  const totalBalance = MOCK_SUPPLIERS.reduce((s, x) => s + x.outstandingBalance, 0);
  const activeCount = MOCK_SUPPLIERS.filter(s => s.status === 'active' || s.status === 'preferred').length;
  const preferredCount = MOCK_SUPPLIERS.filter(s => s.preferred).length;
  const pendingCount = MOCK_SUPPLIERS.filter(s => s.status === 'pending_approval' || s.status === 'pending_review').length;
  const expiringDocs = MOCK_SUPPLIERS.filter(s => s.documents?.some(d => d.status === 'expiring_soon')).length;
  const avgRating = (MOCK_SUPPLIERS.reduce((s, x) => s + (x.rating ?? 0), 0) / MOCK_SUPPLIERS.length).toFixed(1);

  const allCategories = Array.from(new Set(MOCK_SUPPLIERS.flatMap(s => s.categories)));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
              <Truck size={18} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-xl font-black text-gray-900">Suppliers</h1>
              <p className="text-xs text-gray-400">Manage supplier relationships, procurement performance, compliance and financial activity.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link href="/suppliers/analytics" className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              <BarChart3 size={13} />Analytics
            </Link>
            <Link href="/suppliers/reports" className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              <FileText size={13} />Reports
            </Link>
            <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              <Download size={13} />Export
            </button>
            {canCreate && (
              <Link href="/suppliers/new" className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                <Plus size={13} />Add Supplier
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Suppliers" value={MOCK_SUPPLIERS.length} sub="All registered" icon={Truck} color="bg-gray-700" />
          <KpiCard label="Active Suppliers" value={activeCount} sub="+1 this month" icon={CheckCircle2} color="bg-green-500" />
          <KpiCard label="Preferred Suppliers" value={preferredCount} sub="Top performers" icon={Award} color="bg-purple-500" />
          <KpiCard label="Pending Approval" value={pendingCount} sub="Awaiting review" icon={Clock} color="bg-amber-500" href="/suppliers" />
          <KpiCard label="Expiring Documents" value={expiringDocs} sub="Next 30 days" icon={AlertTriangle} color="bg-red-500" />
          <KpiCard label="Avg Rating" value={`${avgRating}/5`} sub="Overall score" icon={Star} color="bg-orange-500" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
          <KpiCard label="Total Procurement Spend" value={`UGX ${(totalSpend / 1000000).toFixed(1)}M`} sub="All time" icon={DollarSign} color="bg-blue-500" />
          <KpiCard label="Outstanding Balance" value={`UGX ${(totalBalance / 1000000).toFixed(1)}M`} sub="Payable to suppliers" icon={TrendingUp} color="bg-orange-500" />
          <KpiCard label="Open Purchase Orders" value={MOCK_SUPPLIERS.reduce((s, x) => s + x.openPOs, 0)} sub="Across all suppliers" icon={ShoppingCart} color="bg-teal-500" />
        </div>

        {/* Alerts */}
        {(pendingCount > 0 || expiringDocs > 0) && (
          <div className="space-y-2">
            {pendingCount > 0 && (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <Clock size={14} className="text-amber-600 flex-shrink-0" />
                <span className="text-sm text-amber-700 font-medium">{pendingCount} supplier(s) awaiting approval review.</span>
                <button className="ml-auto text-xs font-bold text-amber-700 underline">Review</button>
              </div>
            )}
            {expiringDocs > 0 && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <AlertTriangle size={14} className="text-red-600 flex-shrink-0" />
                <span className="text-sm text-red-700 font-medium">{expiringDocs} supplier(s) have documents expiring within 30 days.</span>
                <button className="ml-auto text-xs font-bold text-red-700 underline">View</button>
              </div>
            )}
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Spend Trend */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Supplier Spend Trend</h3>
                <p className="text-xs text-gray-400">Monthly procurement spend (UGX)</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={SPEND_TREND}>
                <defs>
                  <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
                <Tooltip formatter={(v: number) => [`UGX ${(v / 1000000).toFixed(1)}M`, 'Spend']} />
                <Area type="monotone" dataKey="spend" stroke="#f97316" strokeWidth={2} fill="url(#spendGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Spend by Category */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Spend by Category</h3>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={SPEND_BY_CATEGORY} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={2}>
                  {SPEND_BY_CATEGORY.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v: number) => [`UGX ${(v / 1000000).toFixed(1)}M`]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {SPEND_BY_CATEGORY.map((c) => (
                <div key={c.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                    <span className="text-gray-600">{c.name}</span>
                  </div>
                  <span className="font-semibold text-gray-700">UGX {(c.value / 1000000).toFixed(0)}M</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Top Performing Suppliers</h3>
            <div className="space-y-3">
              {[...MOCK_SUPPLIERS].sort((a, b) => (b.overallScore ?? 0) - (a.overallScore ?? 0)).slice(0, 4).map((s, i) => {
                const perf = getPerformanceLevel(s.overallScore ?? 0);
                return (
                  <div key={s.id} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center text-xs font-black text-orange-600">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-900 truncate">{s.name}</div>
                      <div className="text-xs text-gray-400">{s.categories[0]}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-orange-400" style={{ width: `${s.overallScore ?? 0}%` }} />
                      </div>
                      <span className={`text-xs font-bold ${perf.color}`}>{s.overallScore}</span>
                    </div>
                    {s.preferred && <Award size={12} className="text-purple-500 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Performance Distribution */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Performance Distribution</h3>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={PERFORMANCE_DIST} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={120} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {PERFORMANCE_DIST.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Supplier Directory */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h2 className="text-sm font-bold text-gray-900 flex-1">Supplier Directory</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search suppliers..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 w-48"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="preferred">Preferred</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="suspended">Suspended</option>
                  <option value="blacklisted">Blacklisted</option>
                </select>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
                >
                  <option value="all">All Categories</option>
                  {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Supplier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Contact</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Rating</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Total Spend</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Balance</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((s) => {
                  const primaryContact = s.contacts?.find(c => c.isPrimary);
                  return (
                    <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-sm font-black text-orange-600 flex-shrink-0">
                            {s.name.charAt(0)}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-bold text-gray-900">{s.name}</span>
                              {s.preferred && <Award size={11} className="text-purple-500" />}
                            </div>
                            <div className="text-xs text-gray-400">{s.supplierCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {s.categories.slice(0, 2).map(c => (
                            <span key={c} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">{c}</span>
                          ))}
                          {s.categories.length > 2 && <span className="text-xs text-gray-400">+{s.categories.length - 2}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {primaryContact ? (
                          <div>
                            <div className="text-xs font-medium text-gray-700">{primaryContact.fullName}</div>
                            <div className="text-xs text-gray-400">{primaryContact.phone}</div>
                          </div>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star size={11} className="text-amber-400 fill-amber-400" />
                          <span className="text-xs font-bold text-gray-700">{s.rating ?? '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-xs font-semibold text-gray-700">
                        {formatUGX(s.totalSpend)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-semibold ${s.outstandingBalance > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                          {s.outstandingBalance > 0 ? formatUGX(s.outstandingBalance) : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <SupplierStatusBadge status={s.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Link href={`/suppliers/${s.id}`} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="View">
                            <Eye size={13} className="text-gray-500" />
                          </Link>
                          {canCreate && (
                            <Link href={`/suppliers/${s.id}`} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Edit">
                              <Edit size={13} className="text-gray-500" />
                            </Link>
                          )}
                          <Link href="/procurement/rfqs/new" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Create RFQ">
                            <ShoppingCart size={13} className="text-gray-500" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <Truck size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No suppliers found matching your filters.</p>
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-400">{filtered.length} of {MOCK_SUPPLIERS.length} suppliers</span>
            <div className="flex items-center gap-1">
              <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Previous</button>
              <button className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg">1</button>
              <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
